import { CalendarClock, MonitorPlay } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { FormField } from '@/components/forms/FormField'
import { Badge } from '@/components/ui/Badge'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { useAuth } from '@/features/auth/useAuth'
import { familyLmsQuery } from '@/features/lms/api'
import { useChild } from '@/features/lms/useChild'
import { subjectDotStyle } from '@/features/timetable/week'
import { familyPortalLabel, useGuardians } from '@/features/auth/guardians'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { tAny } from '@/i18n'
import { formatDateTime } from '@/utils/format'

/** Family portal → Online learning: what is due, and every course of the class. */
export function FamilyLmsPage() {
  const { t } = useTranslation('lms')
  const guardians = useGuardians()
  useDocumentTitle('Online learning')
  const { user } = useAuth()
  const { children, list, child, select } = useChild()
  const overview = useQuery({ ...familyLmsQuery(child?.student.id ?? 0), enabled: !!child })
  const isStudent = user?.role === 'student'

  return (
    <>
      <PageHeader
        title={t('Online learning')}
        description={
          isStudent
            ? t('Your lessons, resources and homework.')
            : t('Your child’s lessons and homework, and the grades they receive.')
        }
        breadcrumbs={[
          { label: tAny(familyPortalLabel(guardians)), to: '/portal' },
          { label: t('Online learning') },
        ]}
        homeHref="/portal"
        actions={
          list.length > 1 ? (
            <FormField label={t('Child')} hideLabel className="w-full sm:w-64">
              <Select
                value={child?.student.id ?? ''}
                onChange={(e) => select(Number(e.target.value))}
                options={list.map((c) => ({ value: c.student.id, label: c.student.fullName }))}
              />
            </FormField>
          ) : null
        }
      />
      {children.isPending || (child && overview.isPending) ? (
        <LoadingState />
      ) : children.error || overview.error ? (
        <ErrorState onRetry={() => void (children.error ? children.refetch() : overview.refetch())} />
      ) : !child || !overview.data?.section ? (
        <Card>
          <EmptyState
            icon={<MonitorPlay />}
            title={t('No class yet')}
            description={t('Courses appear once the student is enrolled in a class.')}
          />
        </Card>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-3">
          <Card className="lg:order-last">
            <CardHeader
              icon={<CalendarClock />}
              title={t('To hand in')}
              description={overview.data.todo.length ? undefined : t('Nothing due. Well done!')}
            />
            {overview.data.todo.length ? (
              <ul className="divide-y divide-line">
                {overview.data.todo.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/portal/learning/${a.subject.id}?student=${child.student.id}`}
                      className="flex items-start gap-3 px-5 py-3 hover:bg-surface-muted"
                    >
                      <span
                        className="mt-1.5 size-2.5 shrink-0 rounded-full bg-brand-500"
                        style={subjectDotStyle(a.subject.color)}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-ink">{a.title}</span>
                        <span className="block text-xs text-muted">
                          {a.subject.name} · {t('due {{date}}', { date: formatDateTime(a.dueAt) })}
                        </span>
                      </span>
                      {a.overdue ? (
                        <Badge tone="danger" size="sm">
                          {t('Overdue')}
                        </Badge>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>
          <div className="lg:col-span-2">
            <h2 className="mb-3 text-sm font-semibold text-ink">
              {t('Courses of {{section}}', { section: overview.data.section.name })}
            </h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {overview.data.courses.map((c) => (
                <li key={c.subject.id}>
                  <Link
                    to={`/portal/learning/${c.subject.id}?student=${child.student.id}`}
                    className="flex h-full items-start gap-3 rounded-card border border-line bg-surface p-4 shadow-card transition hover:border-brand-300 hover:shadow-raised"
                  >
                    <span
                      className="mt-1 size-3 shrink-0 rounded-full bg-brand-500"
                      style={subjectDotStyle(c.subject.color)}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-ink">{c.subject.name}</span>
                      <span className="block text-xs text-muted">
                        {t('{{count}} contents', { count: c.materials })}
                        {c.nextDue ? ` · ${t('next due {{date}}', { date: formatDateTime(c.nextDue) })}` : ''}
                      </span>
                    </span>
                    {c.toDo ? (
                      <Badge tone="warning" size="sm">
                        {t('{{count}} to do', { count: c.toDo })}
                      </Badge>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  )
}
